import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button} from 'reactstrap';
import {refreshToken, fetchLoggedUser, fetchPinIdentity, clearPinError, logout, clearForceLogout} from '../../slices/auth.slice';
import PinInputKeyboard from '../PinInputKeyboard';
import Avatar from '../Avatar';

const PinScreen = ({onSuccess, onUsePassword}) => {
    const dispatch = useDispatch();
    const pinAttempts = useSelector(state => state.auth.pinAttempts);
    const forceLogout = useSelector(state => state.auth.forceLogout);
    // On a reload with an expired token the in-memory user is gone; fall back to the
    // persisted snapshot so the PIN screen still shows who's logged in.
    const user = useSelector(state => state.auth.user || state.auth.pinUser);

    // After a reload the access token is expired and the in-memory user is gone. If the
    // persisted snapshot is also missing (e.g. first lock after this feature shipped),
    // resolve the identity from the refresh token so the screen still shows who's logged in.
    useEffect(() => {
        if (!user) {
            dispatch(fetchPinIdentity());
        }
    }, [user, dispatch]);

    useEffect(() => {
        if (forceLogout) {
            dispatch(clearForceLogout());
            if (onUsePassword) {
                onUsePassword();
            }
        }
    }, [forceLogout, dispatch, onUsePassword]);

    const handleSubmit = async (pinCode) => {
        try {
            await dispatch(refreshToken(pinCode)).unwrap();
            dispatch(fetchLoggedUser());
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            // Error state handled by slice (pinError, pinAttempts, forceLogout)
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        if (onUsePassword) {
            onUsePassword();
        }
    };

    const handleError = () => {
        dispatch(clearPinError());
    };

    const remaining = Math.max(0, 3 - pinAttempts);

    return (
        <div className="pincode-container d-flex bg-broken align-items-center justify-content-center flex-column p-3 min-vh-100 w-100">
            <div className="mb-4 pb-3 border-bottom border-primary opacity-50 px-5">
                <img src="/logo.svg" alt="" width="200px"/>
            </div>

            {user && (
                <div className="d-flex flex-column align-items-center mb-4">
                    <Avatar
                        imageUrl={user.profilePic?.imageUrl}
                        name={`${user.firstName} ${user.lastName}`}
                        size={64}
                    />
                    <div className="name text-uppercase text-primary fw-bold mt-2">
                        {`${user.firstName} ${user.lastName}`}
                    </div>
                    {user.parentOrganization && (
                        <span className="organization text-white opacity-75 small">
                            {user.parentOrganization?.name}
                        </span>
                    )}
                </div>
            )}

            <PinInputKeyboard onSubmit={handleSubmit} onError={handleError}/>

            {pinAttempts > 0 && remaining > 0 && (
                <div className="text-danger mt-3 small">
                    Invalid PIN — {remaining} attempt{remaining === 1 ? '' : 's'} remaining
                </div>
            )}

            {onUsePassword && (
                <Button color="link" className="key text-decoration-none mt-3 opacity-75" onClick={onUsePassword}>
                    <span className="fas fa-key me-2"/> Use password instead
                </Button>
            )}

            <Button color="link" className="key text-decoration-none mt-2 opacity-50" onClick={handleLogout}>
                <span className="fas fa-sign-out text-danger me-2"/> Logout
            </Button>
        </div>
    );
};

export default PinScreen;
